import { useEffect, useState } from "react";
import { Trash2, Edit2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSystemInformAreaStore } from "../../data/stores/loggerStore";
import { apiCall, useApiCall } from "../../data/useFetcher";
import moment from "moment";
import PrimaryBtn, { LoadMore, MainPaginate } from "../partials/btn";
import { TriggerModal } from "../modals/successmodal";

function SystemInformAreaTable({
  usecase,
  search,
}: {
  usecase?: string;
  search?: string;
}) {
  const [datum, setDatum] = useState<any>({
      docs: [],
      hasNextPage: false,
    }),
    [updater, setUpdater] = useState<any>(null),
    { getDynamicLogger, getSearchLogger } = useSystemInformAreaStore(),
    { onSubmitAPI, loading: isLoading } = useApiCall();

  const onSubmit = async () => {
    await onSubmitAPI({
      url: `/api/v1/system-inform-area`,
      type: "post",
      data: { systemInformArea: updater?.data, type: updater?.isOpen },
      toggleModal: () => {
        setUpdater(null);
        apiCall({
          type: "get",
          url: `/api/v1/system-inform-area${
            usecase === "newly-uploaded" ? `?streamline=week` : ""
          }${search ? `&search=${search}` : ""}`,
          getter: (d: any) =>
            search
              ? getSearchLogger(d)
              : getDynamicLogger(
                  d,
                  usecase === "newly-uploaded" ? "newlyUploadedData" : "allData"
                ),
        });
      },
    });
  };

  return (
    <div className="px-5">
      {datum?.docs?.some?.((ic) => ic?.isChecked) && (
        <div className="flex justify-end items-center gap-3 my-4">
          <PrimaryBtn
            loadingColor="red"
            text="Delete"
            className={
              "w-full text-left p-3 hover:bg-gray-100 text-red-500 rounded-md flex items-center gap-3 border border-dashed border-red-500 relative"
            }
            onClick={() =>
              setUpdater({
                isOpen: "delete",
                data: datum?.docs?.filter((iz) => iz?.isChecked),
              })
            }
          />
        </div>
      )}
      <table className="w-full">
        <thead>
          <tr className="">
            <th className="p-3 text-left font-normal text-gray-600">
              <CheckAllboxes datum={datum} setDatum={setDatum} />
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085]">
              Title
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085]">
              Date
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085]"></th>
          </tr>
        </thead>
        <InviteSystemInformAreaTable
          usecase={usecase}
          datum={datum}
          setDatum={setDatum}
          updater={updater}
          setUpdater={setUpdater}
          onSubmit={onSubmit}
          isLoading={isLoading}
          search={search}
        />
      </table>
    </div>
  );
}

export default SystemInformAreaTable;

export const InviteSystemInformAreaTable = ({
  usecase,
  datum,
  setDatum,
  updater,
  setUpdater,
  onSubmit,
  isLoading,
  search,
}: {
  usecase?: string;
  datum: any;
  setDatum: React.Dispatch<any>;
  updater: any;
  setUpdater: React.Dispatch<any>;
  onSubmit: () => Promise<void>;
  isLoading: string | boolean;
  search?: string;
}) => {
  const { mainSearch, isFound, newlyUploadedData, allData }: any =
      useSystemInformAreaStore(),
    { getDynamicLogger, resetLoggerSearch, getSearch, getSearchLogger } =
      useSystemInformAreaStore(),
    [loading, setLoading] = useState<string | boolean>(false),
    navigate = useNavigate();

  useEffect(() => {
    resetLoggerSearch();
  }, []);

  useEffect(() => {
    if (isFound) setDatum(mainSearch);
    else setDatum(usecase === "newly-uploaded" ? newlyUploadedData : allData);
  }, [isFound, mainSearch, newlyUploadedData, usecase, allData]);

  useEffect(() => {
    if (search) getSearch(search);
    apiCall({
      type: "get",
      url: `/api/v1/system-inform-area?a=a${
        usecase === "newly-uploaded" ? `&streamline=week` : ""
      }${search ? `&search=${search}` : ""}`,
      getter: (d: any) =>
        search
          ? getSearchLogger(d)
          : getDynamicLogger(
              d,
              usecase === "newly-uploaded" ? "newlyUploadedData" : "allData"
            ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usecase, search]);

  useEffect(() => {
    if (search) {
      document.getElementById("Search").addEventListener("search", () => {
        resetLoggerSearch();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleLoadMore = async () => {
    setLoading("loadmore");
    await apiCall({
      type: "get",
      url: `/api/v1/system-inform-area?limit=${datum?.limit * datum?.nextPage}${
        usecase === "newly-uploaded" ? `&streamline=week` : ""
      }${search ? `&search=${search}` : ""}`,
      getter: (d: any) =>
        search
          ? getSearchLogger(d)
          : getDynamicLogger(
              d,
              usecase === "newly-uploaded" ? "newlyUploadedData" : "allData"
            ),
    });
    setLoading(false);
  };

  const [itemOffset, setItemOffset] = useState(0);
  const [range] = useState(10),
    [page, setPage] = useState(1);
  const endOffset = itemOffset + range;

  // if (!datum && !data) return <PageLoader />;
  if (!datum) return;

  const currentItems = datum?.docs?.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(datum?.docs?.length / range);

  const handlePageClick = (event: any) => {
    const newOffset = (event.selected * range) % datum?.docs?.length;
    setItemOffset(newOffset);
    setPage(1 + event?.selected);
  };

  return (
    <>
      <tbody>
        {currentItems?.map((contact: any, i: number) => (
          <tr
            style={{
              background: i % 2 === 0 ? "#F9FAFB" : "transparent",
            }}
            key={contact._id}
            className="h-20"
          >
            <td className="p-3">
              <Checkbox item={contact} datum={datum} setDatum={setDatum} />
            </td>
            <td className="p-3 text-sm text-sec font-medium">
              {contact?.title}
            </td>
            <td className="p-3 text-sm font-medium text-[#667085]">
              {moment(contact?.createdAt)?.format("DD/MM/YY")}
            </td>
            <td className="p-3 flex justify-center items-center h-20 space-x-2 gap-3">
              <div className="relative">
                <PrimaryBtn
                  onClick={async (e) => {
                    e?.stopPropagation();
                    setUpdater({
                      isOpen: "delete",
                      data: [{ ...contact, isDeleted: true }],
                    });
                  }}
                  loadingColor="#667085"
                  width="w"
                  text="Delete"
                  className={
                    "text-[#667085] hover:text-gray-700 flex items-center gap-3"
                  }
                >
                  <Trash2 size={18} />
                </PrimaryBtn>
              </div>
              <div className="relative">
                <PrimaryBtn
                  onClick={async (e) => {
                    e?.stopPropagation();
                    setUpdater({
                      isOpen: "update",
                      data: [contact],
                    });
                  }}
                  loadingColor="#667085"
                  width="w"
                  text="Update"
                  className={
                    "text-[#667085] hover:text-gray-700 flex items-center gap-3"
                  }
                >
                  <Edit2 size={18} />
                </PrimaryBtn>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
      <div className="mt-4 flex justify-center">
        <LoadMore
          next={page === pageCount && datum?.hasNextPage}
          loading={loading === "loadmore"}
          handleLoadMore={handleLoadMore}
        />
      </div>
      {pageCount > 1 && (
        <MainPaginate pageCount={pageCount} handlePageClick={handlePageClick} />
      )}
      {updater?.isOpen && (
        <TriggerModal
          title={`${updater?.isOpen?.toUpperCase()} Area`}
          desc={
            updater?.isOpen === "delete"
              ? `Action done cannot be reversible, Proceed to to delete?`
              : `Proceed to update selected area?`
          }
          onClose={() => setUpdater(null)}
          onClick={() => {
            if (updater?.isOpen === "delete") {
              onSubmit();
            } else {
              navigate("manage-data", { state: { data: updater?.data } });
              setUpdater(null);
            }
          }}
          loading={!!isLoading}
        />
      )}
    </>
  );
};

export const CheckAllboxes = ({ datum, setDatum }) => {
  return (
    <>
      <input
        type="checkbox"
        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 main-border"
        onChange={(e) => {
          let dd = datum?.docs;
          dd = dd?.map((ic) => {
            let dc = ic;
            return { ...dc, isChecked: e?.target?.checked };
          });
          setDatum({ ...datum, docs: dd });
        }}
        checked={
          datum?.docs?.length > 0 && datum?.docs?.every((it) => it?.isChecked)
        }
      />
    </>
  );
};

export const Checkbox = ({ datum, setDatum, item }) => {
  return (
    <>
      <input
        id="checkbox-table-search-1"
        type="checkbox"
        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
        onChange={(e) => {
          let dd = datum?.docs;
          let current = item;
          if (e?.target?.checked) {
            current = {
              ...current,
              isChecked: true,
            };
          } else {
            current = {
              ...current,
              isChecked: false,
            };
          }
          dd = dd?.map((ic) => (ic?._id === item?._id ? current : ic));
          setDatum({ ...datum, docs: dd });
        }}
        checked={item?.isChecked}
      />
    </>
  );
};
